import {
  Order,
  PaginationData,
} from 'constants/interface';
import { InventoryTypeEnum } from 'constants/inventory';
import {
  DeliveryTypeEnum,
  OrderApiStatus,
  OrderStumpTypeEnum,
  OrderTypeEnum,
  OrderUnitEnum,
} from 'constants/order';
import { PaymentMethodEnum } from 'constants/payment';
import {
  buildPaginationParams,
  getPaginationHeaders,
  PaginatedResponse,
  requestClient,
} from 'infra/request';

interface CreateOrderItemProductData {
  product: string
  tray: string
  seedSource: InventoryTypeEnum
}

export interface CreateOrderItemData {
  unit: OrderUnitEnum
  orderQuantity: number
  pruning: boolean
  stumpType: OrderStumpTypeEnum
  stumpQuantity?: number
  plannedSeedDate: string
  plannedDeliveryDate: string
  unitPrice: number
  stumpUnitPrice?: number
  mainProduct: CreateOrderItemProductData
  stockProduct?: CreateOrderItemProductData
}

export interface CreateOrderData {
  isConfirmed: boolean
  type: OrderTypeEnum
  deliveryType: DeliveryTypeEnum
  client: string
  location?: string
  organization: string
  notes?: string
  items: CreateOrderItemData[]
  advancePaymentDate?: string
  advancePaymentMethod?: PaymentMethodEnum
  advancePaymentPrice?: number
  advancePaymentResponsible?: string
  advancePaymentConfirmed: boolean
}

export interface UpdateOrderData {
  id: string
  data: CreateOrderData
}

export interface GetOrderList extends PaginationData {
  status?: OrderApiStatus
  type?: OrderTypeEnum
  organization?: string
  startDate?: string
  endDate?: string
}

export interface OrderApi {
  createOrder(orderData: CreateOrderData): Promise<Order>
  updateOrder(orderData: UpdateOrderData): Promise<Order>
  getOrder(orderId: string): Promise<Order>
  getOrderList(filter: GetOrderList): Promise<PaginatedResponse<Order>>
  setOrderStatus(orderId: string, status: OrderApiStatus): Promise<Order>
}

const orderApiBuilder = (): OrderApi => {
  const createOrder = async (
    orderData: CreateOrderData,
  ): Promise<Order> => {
    const params: Record<string, unknown> = {
      ...orderData,
    };
    const result = await requestClient.post('/orders', params);

    const createdOrder: Order = result.data as Order;
    return createdOrder;
  };

  const updateOrder = async (
    orderData: UpdateOrderData,
  ): Promise<Order> => {
    const { id: orderId, data } = orderData;
    const params: Record<string, unknown> = { ...data };
    const result = await requestClient.put(`/orders/${orderId}`, params);
    const updatedOrder: Order = result.data as Order;
    return updatedOrder;
  };

  const getOrder = async (
    orderId: string,
  ): Promise<Order> => {
    const result = await requestClient.get(`/orders/${orderId}`);
    const order: Order = result.data as Order;
    return order;
  };

  const getOrderList = async (
    filterData: GetOrderList,
  ): Promise<PaginatedResponse<Order>> => {
    const params = buildPaginationParams(filterData);
    const result = await requestClient.get('/orders', params);
    const pagination = getPaginationHeaders(result);
    const orderList: Order[] = result.data as Order[];
    return {
      pagination,
      data: orderList,
    };
  };

  const setOrderStatus = async (
    orderId: string,
    status: OrderApiStatus,
  ): Promise<Order> => {
    const params: Record<string, unknown> = {
      status,
    };
    const result = await requestClient.post(`/orders/${orderId}/status`, params);

    const updatedOrder: Order = result.data as Order;
    return updatedOrder;
  };

  return {
    createOrder,
    updateOrder,
    getOrder,
    getOrderList,
    setOrderStatus,
  };
};

export const orderApi = orderApiBuilder();
